// Indisponibilités (D-009, D-114) : « cette personne est-elle indisponible pour CETTE occurrence, et pourquoi ? ».
// Équivalent exact de la fonction SQL unavailability_for (migration 20261101110000_indispos_semaine.sql) : mêmes
// règles, mêmes cas (availability.cases.json, rejoués par vitest ET par le banc). Logique pure.
//
// Une indisponibilité est une PÉRIODE (dates, case « médical », jamais la nature) ou une RÈGLE de la semaine : un ou
// plusieurs jours, toute la journée ou une plage (« à partir de 18 h », « jusqu'à 12 h 45 »), jusqu'à une date ou,
// sans date, jusqu'à la fin de la saison (30 juin, D-115).
//  - Entraînement (et autre rendez-vous) : absente d'office dès que la plage couvre le début.
//  - Match : elle doit pouvoir arriver à la salle pour le coup d'envoi, route comprise (0 à domicile, temps de route
//    de l'app depuis le gymnase du club à l'extérieur, arrondi aux 5 min). Arrivée après le rendez-vous de la
//    convocation (à l'extérieur : l'arrivée à la salle ; à domicile : le rendez-vous du club) mais avant le coup
//    d'envoi : présente « sans échauffement ». Après le coup d'envoi : absente d'office.
import { roundUp5 } from './trip';
import { seasonOf } from './season';

export interface Period { id?: string; personId: string; startsOn: string; endsOn: string; isMedical: boolean }
export interface WeeklyRule {
  id: string; personId: string;
  /** 1 = lundi … 7 = dimanche */
  weekdays: number[];
  /** « 18:00 » : indisponible à partir de ; vide : dès le matin. */
  fromTime: string | null;
  /** « 12:45 » : jusqu'à ; vide : jusqu'au soir. */
  toTime: string | null;
  /** Le jour où elle a été posée (elle ne réécrit pas les séances passées). */
  startsOn: string;
  /** Inclus ; vide : fin de la saison. */
  untilOn: string | null;
  note?: string | null;
}
export type OccKind = 'training' | 'match' | 'other';
/** Une occurrence vue par la règle : le jour, l'heure du début (minutes depuis minuit, heure du club), la route et
 *  l'avance du rendez-vous (match seulement). */
export interface OccInfo { date: string; startMin: number; kind: OccKind; travelMin: number; leadMin: number }
export interface Verdict {
  state: 'off' | 'late';
  source: 'period' | 'rule';
  ref: string | null;
  medical: boolean;
  /** « mercredi soir », « dimanche jusqu’à 12 h 45 » (règle). */
  label: string | null;
  /** L'heure où elle est libre (règle « jusqu'à »), « 12:45 ». */
  freeAt: string | null;
  travelMin: number;
  /** Le rendez-vous (minutes depuis minuit), match seulement. */
  meetMin: number | null;
}

const DAYS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const MONTHS_SHORT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parse = (s: string) => { const [y, m, d] = s.split('-').map(Number); return new Date(y!, m! - 1, d!); };

/** 1 = lundi … 7 = dimanche */
export const isoDow = (date: string) => parse(date).getDay() || 7;
/** « 18:00 » → 1080 */
export const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h! * 60 + (m ?? 0); };
/** 1080 → « 18:00 » */
export const fromMin = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/** Le dernier jour de la saison d'un jour : le 30 juin (saison FFBB du 1er juillet au 30 juin, D-115). */
export const seasonEnd = (day: string): string => seasonOf(day).to;
/** Le dernier jour d'une règle : sa date, sinon la fin de la saison où elle a été posée. */
export const ruleEnd = (r: WeeklyRule) => r.untilOn ?? seasonEnd(r.startsOn);
/** La règle vaut-elle ce jour-là (jour de la semaine, entre sa pose et sa fin) ? */
export const ruleActiveOn = (r: WeeklyRule, date: string) => r.weekdays.includes(isoDow(date)) && r.startsOn <= date && date <= ruleEnd(r);
/** La règle est-elle en cours ou à venir à ce jour (pas finie) ? */
export const ruleCurrent = (r: WeeklyRule, today: string) => ruleEnd(r) >= today;

/** « 18 h », « 12 h 45 » */
export const hm = (t: string) => { const m = toMin(t); return `${Math.floor(m / 60)} h${m % 60 ? ` ${String(m % 60).padStart(2, '0')}` : ''}`; };
/** « le mercredi », « le lundi et le jeudi », « tous les jours » */
export function weekdaysText(days: number[]): string {
  const l = [...new Set(days)].sort((a, b) => a - b).map(d => `le ${DAYS[d - 1]}`);
  if (l.length >= 7) return 'tous les jours';
  return l.length < 2 ? (l[0] ?? '') : `${l.slice(0, -1).join(', ')} et ${l[l.length - 1]}`;
}
/** La plage : « à partir de 18 h », « jusqu’à 12 h 45 », « de 14 h à 16 h », « toute la journée ». */
export function windowText(r: Pick<WeeklyRule, 'fromTime' | 'toTime'>): string {
  if (r.fromTime && r.toTime) return `de ${hm(r.fromTime)} à ${hm(r.toTime)}`;
  if (r.fromTime) return `à partir de ${hm(r.fromTime)}`;
  if (r.toTime) return `jusqu’à ${hm(r.toTime)}`;
  return 'toute la journée';
}
/** « le mercredi à partir de 18 h », « le vendredi » (toute la journée : sans plage). */
export function ruleText(r: Pick<WeeklyRule, 'weekdays' | 'fromTime' | 'toTime'>): string {
  const w = weekdaysText(r.weekdays);
  return r.fromTime || r.toTime ? `${w} ${windowText(r)}` : w;
}
/** « 31 janvier », « 1er mars » ; court : « 31 janv. » */
export function dayMonth(date: string, short = false): string {
  const d = parse(date);
  return `${d.getDate() === 1 ? '1er' : d.getDate()} ${(short ? MONTHS_SHORT : MONTHS)[d.getMonth()]}`;
}
/** « jusqu’au 31 janvier », « toute la saison » */
export const untilText = (r: Pick<WeeklyRule, 'untilOn'>, short = false) => (r.untilOn ? `jusqu’au ${dayMonth(r.untilOn, short)}` : 'toute la saison');
/** La règle en une phrase : « Le mercredi à partir de 18 h, jusqu’au 31 janvier. », « Le mercredi, toute la journée, jusqu’au 16 décembre. » */
export function ruleSentence(r: Pick<WeeklyRule, 'weekdays' | 'fromTime' | 'toTime' | 'untilOn'>): string {
  const s = `${weekdaysText(r.weekdays)}${r.fromTime || r.toTime ? ` ${windowText(r)}` : ', toute la journée'}, ${untilText(r)}.`;
  return s.charAt(0).toUpperCase() + s.slice(1);
}
/** Pour une séance (le coach) : « mercredi soir », « mercredi dès 14 h », « dimanche jusqu’à 12 h 45 », « vendredi ». */
export function ruleShort(dow: number, r: Pick<WeeklyRule, 'fromTime' | 'toTime'>): string {
  const d = DAYS[dow - 1] ?? '';
  if (r.fromTime && r.toTime) return `${d} de ${hm(r.fromTime)} à ${hm(r.toTime)}`;
  if (r.fromTime) return toMin(r.fromTime) >= 18 * 60 ? `${d} soir` : `${d} dès ${hm(r.fromTime)}`;
  if (r.toTime) return `${d} jusqu’à ${hm(r.toTime)}`;
  return d;
}
/** « mercredi » */
export const dayName = (dow: number) => DAYS[dow - 1] ?? '';

/** L'occurrence vue par la règle. `tripMin` : le temps de route en cache (match à l'extérieur), sinon inconnu (0). */
export function occInfo(o: { date: string; start: Date; kind: OccKind; isHome?: boolean | null; tripMin?: number | null },
  leads: { arrive: number; home: number }): OccInfo {
  const match = o.kind === 'match';
  return {
    date: o.date, startMin: o.start.getHours() * 60 + o.start.getMinutes(), kind: o.kind,
    travelMin: match && o.isHome === false && o.tripMin != null && Number.isFinite(o.tripMin) ? roundUp5(o.tripMin) : 0,
    leadMin: match ? (o.isHome === false ? leads.arrive : leads.home) : 0,
  };
}
/** Les avances par défaut d'un club (D-053 : arrivée 1 h avant à l'extérieur ; rendez-vous 45 min avant à domicile). */
export const DEFAULT_LEADS = { arrive: 60, home: 45 };

/** LA règle : rien (disponible), ou la raison la plus forte (période, puis règle ; absente avant « sans échauffement »). */
export function availabilityFor(personId: string, occ: OccInfo, periods: readonly Period[], rules: readonly WeeklyRule[]): Verdict | null {
  type W = { source: 'period' | 'rule'; ref: string | null; medical: boolean; s: number; u: number; toTime: string | null; label: string | null; prio: number };
  const dow = isoDow(occ.date);
  const ws: W[] = [
    ...periods.filter(p => p.personId === personId && p.startsOn <= occ.date && occ.date <= p.endsOn)
      .map((p): W => ({ source: 'period', ref: p.id ?? null, medical: p.isMedical, s: 0, u: 1440, toTime: null, label: null, prio: 0 })),
    ...rules.filter(r => r.personId === personId && ruleActiveOn(r, occ.date))
      .map((r): W => ({ source: 'rule', ref: r.id, medical: false, s: r.fromTime ? toMin(r.fromTime) : 0, u: r.toTime ? toMin(r.toTime) : 1440,
        toTime: r.toTime, label: ruleShort(dow, r), prio: 1 })),
  ];
  const t0 = occ.startMin;
  const out: (W & { state: 'off' | 'late' })[] = [];
  for (const w of ws) {
    let state: 'off' | 'late' | null = null;
    if (occ.kind === 'match') {
      if (w.s <= t0 && w.u + occ.travelMin > t0) state = 'off';
      else if (w.s <= t0 && w.u + occ.travelMin > t0 - occ.leadMin) state = 'late';
    } else if (w.s <= t0 && w.u > t0) state = 'off';
    if (state) out.push({ ...w, state });
  }
  out.sort((a, b) => Number(a.state === 'late') - Number(b.state === 'late') || a.prio - b.prio || Number(b.medical) - Number(a.medical)
    || String(a.ref).localeCompare(String(b.ref)));
  const v = out[0];
  if (!v) return null;
  return { state: v.state, source: v.source, ref: v.ref, medical: v.medical, label: v.label, freeAt: v.toTime ? v.toTime.slice(0, 5) : null,
    travelMin: occ.travelMin, meetMin: occ.leadMin > 0 ? t0 - occ.leadMin : null };
}

/** « 12h15 » (heure de séance, comme ailleurs dans l'app) */
export const clock = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}h${String(min % 60).padStart(2, '0')}`;

/** Une séance de la semaine d'une équipe (son jour, son heure), pour l'aperçu d'une règle. */
export interface WeekSlot { dow: number; startMin: number }
/** Un match à venir, pour l'aperçu d'une règle : « du dimanche 22 nov. (13h00) ». */
export interface MatchSlot { occ: OccInfo; label: string }

/** Ce que fait la règle, dit avant d'enregistrer (D-114) : « Absente d’office aux entraînements du mercredi (19h00) ;
 *  les matchs ne sont pas concernés. » Mots accordés par l'écran : `off` « Absente d’office » (soi) ou « Kenza sera
 *  absente d’office », `offLower` « absente d’office », `late` « sans échauffement ». */
export function ruleEffect(r: Pick<WeeklyRule, 'weekdays' | 'fromTime' | 'toTime'>, trainings: readonly WeekSlot[], matches: readonly MatchSlot[],
  words: { off: string; offLower: string; late: string }): string {
  const on = (date: string): WeeklyRule => ({ id: 'x', personId: 'x', weekdays: r.weekdays, fromTime: r.fromTime, toTime: r.toTime, startsOn: date, untilOn: date });
  const byDay = new Map<number, Set<number>>();
  for (const t of trainings) {
    const date = dateOfDow(t.dow);
    if (availabilityFor('x', { date, startMin: t.startMin, kind: 'training', travelMin: 0, leadMin: 0 }, [], [on(date)])?.state === 'off')
      byDay.set(t.dow, (byDay.get(t.dow) ?? new Set()).add(t.startMin));
  }
  const parts: string[] = [];
  if (byDay.size) {
    const days = [...byDay].sort((a, b) => a[0] - b[0]).map(([d, ms]) => `du ${dayName(d)} (${[...ms].sort((a, b) => a - b).map(clock).join(', ')})`);
    parts.push(`${words.off} aux entraînements ${days.length < 2 ? days[0] : `${days.slice(0, -1).join(', ')} et ${days[days.length - 1]}`}`);
  }
  const hit = matches.map(m => ({ m, v: availabilityFor('x', m.occ, [], [on(m.occ.date)]) })).filter(x => x.v);
  for (const x of hit.slice(0, 2)) parts.push(`match ${x.m.label} : ${x.v!.state === 'off' ? words.offLower : words.late}`);
  if (hit.length > 2) parts[parts.length - 1] += ` (et ${hit.length - 2} autre${hit.length > 3 ? 's' : ''})`;
  if (!hit.length && (r.fromTime || r.toTime)) parts.push(byDay.size ? 'les matchs ne sont pas concernés' : 'aucun match prévu n’est concerné');
  if (!parts.length) return 'Aucune séance prévue n’est concernée pour l’instant.';
  const s = parts.join(' ; ');
  return `${s.charAt(0).toUpperCase()}${s.slice(1)}.`;
}

/** Un jour quelconque qui tombe ce jour de la semaine (pour juger une séance hebdomadaire). */
function dateOfDow(dow: number): string {
  return ymd(new Date(2026, 0, 4 + dow));   // le 5 janvier 2026 est un lundi
}
